import React, { useRef, useState } from "react";
import { Box, Typography, Button, Grid, Drawer } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import MainLayout from "../layouts/Main";
import { observer } from "mobx-react";
import { useStores } from "../stores";
import SouvenirSelectionWarningModal from "../components/modals/SouvenirSelectionWarning";
import type { TransactionType } from "../models/Souvenir";
import StyleVariables from "../assets/styles/Variable.module.scss";
import SimpleAccordion from "../components/accordions/Simple";
import DepositForm from "../components/forms/deposits/Deposit";
import CustomerForm from "../components/forms/Customer";
import SouvenirForm from "../components/forms/Souvenir";
import SouvenirAccordion from "../components/accordions/Souvenirs";
import DepositBillModal from "../components/modals/DepositBill";
import BillModal from "../components/modals/Bill";
import DepositBill from "../components/bills/Deposit";
import DepositCancelBill from "../components/bills/cancels/Deposit";
import {
  ScrollToTop as ScrollToTopUtil,
  ScrollToElementId as ScrollToElementIdUtil,
} from "../utils/ScrollToElement";
import {
  Create as CreateCustomerModel,
  Customer as CustomerModel,
} from "../models/Customer";
import {
  CreateNew as CreateNewDepositModel,
  Deposit as DepositModel,
  DepositRenew as DepositRenewModel,
  CancelDeposit as CancelDepositModel,
} from "../models/Deposit";
import { Return as ReturnModel } from "../models/Return";
import { CancelReasonForm as CancelReasonFormModel } from "../models/MasterData";
import LoadProgressing from "../components/LoadProgressing";
import InformationModal from "../components/modals/Information";
import AlertSnackbar from "../components/snackbars/Alert";
import DepositListTable from "../components/tables/DepositList";
import CancelBillModal from "../components/modals/CancelBill";
import { Create as CreateCustomerService } from "../services/Customer";
import {
  CreateDepositNew as CreateDepositNewService,
  SubmitDepositById as SubmitDepositByIdService,
  GetLatestDeposits as GetLatestDepositsService,
  GetDepositById as GetDepositByIdService,
  CancelDeposit as CancelDepositService,
} from "../services/Deposit";
import { OnPrintPdf as OnPrintPdfService } from "../services/Storage";
import { v4 as uuidv4 } from "uuid";

type Props = {};

const Deposit = observer((props: Props) => {
  const { MainStore, DepositStore, CustomerStore, PaymentStore, SouvenirStore } = useStores();
  const [isBillModalOpen, setIsBillModalOpen] = useState<boolean>(false);
  const [isLoadProgressing, setIsLoadProgressing] = useState<boolean>(false);
  const [isFailModalOpen, setIsFailModalOpen] = useState<boolean>(false);
  const [messageFailModal, setMessageFailModal] = useState<string>("");
  const [scrollToElementId, setScrollToElementId] = useState<string>("");
  const [isOpenSuccessSnackbar, setIsOpenSuccessSnackbar] =
    useState<boolean>(false);
  const [isOpenList, setIsOpenList] = useState<boolean>(false);
  const [isLoadingList, setIsLoadingList] = useState<boolean>(false);
  const [deposits, setDeposits] = useState<any[]>([]);
  const [isCompleteBillModalOpen, setIsCompleteBillModalOpen] =
    useState<boolean>(false);
  const [isCompleteBillCancelModalOpen, setIsCompleteBillCancelModalOpen] =
    useState<boolean>(false);
  const [deposit, setDeposit] = useState<DepositModel | null>(null);
  const [customer, setCustomer] = useState<CustomerModel | null>(null);
  const [isBillRenew, setIsBillRenew] = useState<boolean>(false);
  const [depositRenew, setDepositRenew] = useState<DepositRenewModel | null>(
    null
  );
  const [isCancelBillModalOpen, setIsCancelBillModalOpen] =
    useState<boolean>(false);
  const [submitCancelLimit, setSubmitCancelLimit] = useState<number>(0);
  const [submitCancelRemaining, setSubmitCancelRemaining] = useState<number>(0);
  const [submitCancelAfter, setSubmitCancelAfter] = useState<number>(0);
  const [requestId, setRequestId] = useState<string>("");
  const [isCheckingSouvenirs, setIsCheckingSouvenirs] = useState<boolean>(false);
  const [isSouvenirSelectionWarningModalOpen, setIsSouvenirSelectionWarningModalOpen] =
    useState<boolean>(false);
  const isCheckingSouvenirsRef = useRef<boolean>(false);
  const souvenirScrollTargetRef = useRef<string | null>(null);

  const onReset = () => {
    SouvenirStore.onReset("deposit");
    DepositStore.onClearStore();
    CustomerStore.onClearStore();
    ScrollToTopUtil();
  };

  const onSubmit = () => {
    setRequestId(uuidv4());
    const isValidFormProduct = DepositStore.onValidateForm();
    const isValidFormPayment = PaymentStore.onValidateForm(
      DepositStore.form.paymentType || "",
      DepositStore.form.cardPay || 0,
      DepositStore.form.chargeCard || 0,
      DepositStore.form.netPay || 0
    );
    if (
      !isValidFormProduct ||
      DepositStore.form.depositPrice < 0 ||
      (DepositStore.form.productPercentage &&
        DepositStore.form.depositPrice > DepositStore.form.standardPoor)
    ) {
      setScrollToElementId("deposit-information-content");
      setMessageFailModal(
        DepositStore.form.depositPrice > DepositStore.form.standardPoor
          ? "ราคารับฝากสูงเกินมาตรฐาน กรุณาตรวจสอบข้อมูลการฝากอีกครั้ง"
          : "กรุณาตรวจสอบข้อมูลการฝากให้ถูกต้อง และลองทำรายการใหม่อีกครั้ง"
      );
      setIsFailModalOpen(true);
      return;
    }
    if (!isValidFormPayment) {
      setScrollToElementId("payment-information-content");
      setMessageFailModal(
        "กรุณาตรวจสอบข้อมูลการชำระเงินให้ถูกต้อง และลองทำรายการใหม่อีกครั้ง"
      );
      setIsFailModalOpen(true);
      return;
    }
    void checkBeforePreview("deposit", () => {
      setIsBillModalOpen(true);
    });
  };

  const onCloseFailModal = () => {
    setIsFailModalOpen(false);
    setMessageFailModal("");
    scrollToElementId && ScrollToElementIdUtil(scrollToElementId);
    setScrollToElementId("");
  };

  const onCreateDeposit = async () => {
    if (isLoadProgressing) return;
    setIsBillModalOpen(false);
    setIsLoadProgressing(true);

    const souvenirSelection =
      await SouvenirStore.onPrepareForSubmit("deposit");

    if (!souvenirSelection.success) {
      setIsLoadProgressing(false);
      setMessageFailModal(souvenirSelection.message);
      setIsFailModalOpen(true);
      return;
    }
    const customerBody: CreateCustomerModel | null = CustomerStore.getBody();
    const depositBody = DepositStore.getBody();
    const bodyNewDeposit: CreateNewDepositModel = {
      ...depositBody,
    };
    let customerId: string = "";
    let responseCreateDeposit: any = null;
    // Create Customer
    if (customerBody) {
      const responseCustomer = await onCreateCustomer(customerBody);
      if (!responseCustomer) return;
      customerId = responseCustomer?.id;
    }
    // /.Create Customer

    // Create New Deposit
    responseCreateDeposit = await onCreateNewDeposit({
      ...bodyNewDeposit,
      ...(customerId && { customerId }),
      souvenirs: souvenirSelection.data,
    });
    // /.Create New Deposit

    if (!responseCreateDeposit) {
      setIsLoadProgressing(false);
      return;
    }

    // Submit PDF
    await onSubmitDeposit(responseCreateDeposit.id);
    // /.Submit PDF
    setIsLoadProgressing(false);
    setIsOpenSuccessSnackbar(true);
    onReset();
  };

  const onCreateCustomer = async (body: CreateCustomerModel) => {
    const response = await CreateCustomerService(body);
    if (!response.success) {
      setMessageFailModal(
        `${
          response.message || "สร้างข้อมูลลูกค้าไม่สำเร็จ กรุณาลองใหม่อีกครั้ง"
        } (${response.code})`
      );
      setIsFailModalOpen(true);
      setIsLoadProgressing(false);
      return false;
    }
    return response.data;
  };

  const onCreateNewDeposit = async (body: CreateNewDepositModel) => {
    const response = await CreateDepositNewService(body, requestId);
    if (!response.success) {
      setMessageFailModal(
        `${
          response.message || "สร้างข้อมูลการฝากไม่สำเร็จ กรุณาลองใหม่อีกครั้ง"
        } (${response.code})`
      );
      setIsFailModalOpen(true);
      setIsLoadProgressing(false);
      return false;
    }
    return response.data;
  };

  const onSubmitDeposit = async (id: string) => {
    const response = await SubmitDepositByIdService(id);
    if (!response.success) {
      setMessageFailModal(
        `${
          response.message ||
          "เกิดข้อผิดพลาดในการสร้างบิล กรุณาพิมพ์บิลใหม่ในรายการประวัติการฝาก"
        } (${response.code})`
      );
      setIsFailModalOpen(true);
      setIsLoadProgressing(false);
      return false;
    }
  };

  const onOpenList = () => {
    getDeposits();
    setIsOpenList(true);
  };

  const onCloseList = () => {
    setIsOpenList(false);
    setDeposits([]);
  };

  const getDeposits = async () => {
    setIsLoadingList(true);
    const response = await GetLatestDepositsService();
    response.success && setDeposits(response.data);
    setIsLoadingList(false);
  };

  const onOpenCompleteBillModal = async (id: string) => {
    setIsLoadProgressing(true);
    const response = await GetDepositByIdService(id);
    setIsLoadProgressing(false);
    let findDepositReturned = null;
    if (response.success && response.data) {
      const { deposit, depositPrev } = response.data;
      const souvenirs = Array.isArray(deposit.souvenirs)
        ? deposit.souvenirs
        : [];
      setIsOpenList(false);
      if (!deposit.refInvoiceNoPrev) {
        setDeposit({
          currentStatus: "good",
          standardExcellent: 0,
          standardGood: 0,
          standardFair: 0,
          standardPoor: 0,
          ...deposit,
          souvenirs,
        });
      } else {
        findDepositReturned = depositPrev.returns?.find(
          (item: ReturnModel) => item.refInvoiceNoNext === deposit.refInvoiceNo
        );
        setIsBillRenew(true);
        setDeposit({
          ...depositPrev,
          souvenirs,
          period: deposit.period,
          userFirstName: deposit.userFirstName,
          userLastName: deposit.userLastName,
          branchName: deposit.branchName,
          branchCode: deposit.branchCode,
          createdAt: deposit.createdAt,
          cancelAt: deposit.cancelAt || "",
          cancelByUserId: deposit.cancelByUserId || "",
          cancelByUserFirstName: deposit.cancelByUserFirstName || "",
          cancelByUserLastName: deposit.cancelByUserLastName || "",
          ...(deposit.cancelReason && { cancelReason: deposit.cancelReason }),
          ...(deposit.cancelMisc && { cancelMisc: deposit.cancelMisc }),
          ...(deposit.cancelReceiptCopy && {
            cancelReceiptCopy: deposit.cancelReceiptCopy,
          }),
          ...(findDepositReturned && {
            percentInterestPerMonth:
              findDepositReturned.percentInterestPerMonth,
            percentInterest: findDepositReturned.percentInterest,
            interest: findDepositReturned.interest,
            receipt: findDepositReturned.receipt,
            receiptCopy: findDepositReturned.receiptCopy,
            invoiceDate: findDepositReturned.invoiceDate,
            totalPay: findDepositReturned.totalPay,
            cardPay: findDepositReturned.cardPay,
            percentChargeCard: findDepositReturned.percentChargeCard,
            chargeCard: findDepositReturned.chargeCard,
            netPay: findDepositReturned.netPay,
          }),
        });
        const renewBillForm: DepositRenewModel = {
          refInvoiceNo: deposit.refInvoiceNo,
          newDepositPrice: deposit.depositPrice,
          reducePrice: 0,
          increasePrice: 0,
          increasePriceStandard: 0,
          purchaseType: "",
          purchasePriceStandard: 0,
          purchasePriceNegotiation: 0,
          purchasePriceCurrentStatus: "good",
          purchaseStandardExcellent: 0,
          purchaseStandardGood: 0,
          purchaseStandardFair: 0,
          purchaseStandardPoor: 0,
          goldSellPrice: deposit.goldSellPrice,
          goldPurchasePrice: deposit.goldPurchasePrice,
          goldPriceDateTime: "",
          receipt: deposit.receipt,
          receiptCopy: deposit.receiptCopy,
        };
        setDepositRenew({
          ...renewBillForm,
        });
      }
      if (deposit.customer) {
        setCustomer(deposit.customer);
      }
      !deposit.cancelAt
        ? setIsCompleteBillModalOpen(true)
        : setIsCompleteBillCancelModalOpen(true);
    }
  };
  const onCloseCompleteBillModal = () => {
    setIsCompleteBillModalOpen(false);
    setDeposit(null);
    setIsBillRenew(false);
    setDepositRenew(null);
    setCustomer(null);
  };
  const onCloseCompleteCancelBillModal = () => {
    setIsCompleteBillCancelModalOpen(false);
    setDeposit(null);
    setIsBillRenew(false);
    setDepositRenew(null);
    setCustomer(null);
  };



  const onPrintBillPdfHistory = async (id: string, printerA4: boolean) => {
    setIsCompleteBillModalOpen(false);
    setIsLoadProgressing(true);
    const response = await OnPrintPdfService(id, printerA4);
    setIsLoadProgressing(false);
    if (!response.success) {
      setMessageFailModal(
        `${response.message || "เกิดข้อผิดพลาดในการพิมพ์ข้อมูลบิลการฝาก"} (${
          response.code
        })`
      );
      setIsFailModalOpen(true);
      return false;
    }
    onCloseCompleteBillModal();
  };

  const onOpenCancelBillModal = (deposit: DepositModel) => {
    onCloseList();
    setDeposit(deposit);
    setIsCancelBillModalOpen(true);
  };

  const onCloseCancelBillModal = () => {
    setIsCancelBillModalOpen(false);
    setDeposit(null);
    setSubmitCancelLimit(0);
    setSubmitCancelRemaining(0);
    setSubmitCancelAfter(0);
  };

  const onCancelDeposit = async (
    form: CancelReasonFormModel,
    password?: string
  ) => {
    const body: CancelDepositModel = {
      cancelReasonId: form.reason.id,
      cancelMisc: form.misc,
      ...(password && { masterPassword: password }),
    };
    if (deposit?.id) {
      const response = await CancelDepositService(deposit?.id, body);
      if (!response?.success) {
        if (response.rateLimit?.remaining || response.rateLimit?.retryAfter) {
          setSubmitCancelLimit(response.rateLimit?.limit);
          setSubmitCancelRemaining(response.rateLimit?.remaining);
          setSubmitCancelAfter(response.rateLimit?.retryAfter);
          return response;
        } else {
          setSubmitCancelLimit(0);
          setSubmitCancelRemaining(0);
          setSubmitCancelAfter(0);
          setMessageFailModal(
            `${
              response.message ||
              "ยกเลิกรายการฝากไม่สำเร็จ กรุณาลองใหม่อีกครั้ง"
            } (${response.code})`
          );
          setIsFailModalOpen(true);
          return response;
        }
      }
      onCloseCancelBillModal();
      onOpenList();
    }
  };

  const checkBeforePreview = async (
    transactionType: TransactionType,
    onOpenPreview: () => void
  ) => {
    if (isCheckingSouvenirsRef.current) return;

    isCheckingSouvenirsRef.current = true;
    setIsCheckingSouvenirs(true);

    try {
      const result =
        await SouvenirStore.onCheckBeforePreview(transactionType);

      if (result === "stale") return;

      if (result === "warning") {
        setIsSouvenirSelectionWarningModalOpen(true);
        return;
      }

      onOpenPreview();
    } finally {
      isCheckingSouvenirsRef.current = false;
      setIsCheckingSouvenirs(false);
    }
  };

  const onScrollToSouvenirs = () => {
    const sectionId = souvenirScrollTargetRef.current;
    if (!sectionId) return;

    souvenirScrollTargetRef.current = null;
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const onBackToSouvenirs = async (sectionId: string) => {
    const wasExpanded = SouvenirStore.expanded;
    souvenirScrollTargetRef.current = sectionId;

    await SouvenirStore.onExpandedChange(true);

    if (wasExpanded) {
      requestAnimationFrame(onScrollToSouvenirs);
    }
  };

  return (
    <MainLayout>
      <>
        <Box
          display={{
            xs: MainStore.isOpenSidebar ? "block" : "flex",
            md: "flex",
          }}
          justifyContent={"space-between"}
          alignItems={"center"}
          gap={2}
          textAlign={"center"}
        >
          <Typography
            variant="h6"
            fontWeight={"bold"}
            color={StyleVariables["color-header"]}
          >
            การฝาก
          </Typography>
          <Box>
            <Button variant="contained-deposit" onClick={onOpenList}>
              ดูประวัติการฝาก
            </Button>
          </Box>
        </Box>

        <Box marginTop={4}>
          {/* Form Deposit */}
          <SimpleAccordion
            id="deposit-information"
            title="ข้อมูลการฝาก"
            defaultExpanded={true}
          >
            <DepositForm />
          </SimpleAccordion>
          {/* Form Deposit */}

          {/* Form Customer */}
          <SimpleAccordion
            id="customer-information"
            title="ข้อมูลลูกค้า/ภาพสินค้า"
          >
            <CustomerForm type="DEPOSIT" />
          </SimpleAccordion>
          {/* /.Form Customer */}
          
          {/* Form Souvenir */}
          <SouvenirAccordion
            id="deposit-souvenir-information"
            expanded={SouvenirStore.expanded}
            onEntered={onScrollToSouvenirs}
            onChange={(expanded) => {
              void SouvenirStore.onExpandedChange(expanded);
            }}
            selectedNames={SouvenirStore.items
              .filter((item) => item.selectedQuantity > 0)
              .map((item) => item.souvenirName)}
          >
            <SouvenirForm
              query={{
                transactionType: "deposit",
                price: DepositStore.form.depositPrice,
              }}
              contextKey={`${DepositStore.form.categoryCode}:${DepositStore.form.productCode}`}
            />
          </SouvenirAccordion>
          {/* /.Form Souvenir */}
        </Box>

        {/* Button */}
        <Grid
          container
          spacing={2}
          justifyContent={"center"}
          marginTop={3}
          flexDirection={{ xs: "column-reverse", md: "row" }}
        >
          <Grid item xs={12} md={6} lg={4}>
            <Button variant="outlined" fullWidth onClick={() => onReset()}>
              เริ่มใหม่
            </Button>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Button variant="contained-success" fullWidth onClick={onSubmit}>
              ทำรายการ
            </Button>
          </Grid>
        </Grid>
        {/* /.Button */}

        {/* Preview Bill Modal */}
        <DepositBillModal
          isOpen={isBillModalOpen}
          onSaveAndPrint={onCreateDeposit}
          onClose={() => {
            setIsBillModalOpen(false);
          }}
        >
          <DepositBill
            deposit={DepositStore.form}
            customer={CustomerStore.form}
            depositRenew={DepositStore.formRenew}
            isBillRenew={DepositStore.isBillRenew}
            souvenirs={SouvenirStore.billSouvenirs}
          />
        </DepositBillModal>
        {/* /.Preview Bill Modal */}

        {/*  Table List */}
        <Drawer anchor={"bottom"} open={isOpenList} onClose={onCloseList}>
          <>
            <Box
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"center"}
              gap={1}
              paddingY={1}
            >
              <Typography
                variant="h6"
                fontWeight={"bold"}
                color={StyleVariables["color-header"]}
              >
                ประวัติการฝากล่าสุด
              </Typography>
              <CloseIcon
                sx={{
                  fontSize: 32,
                  color: StyleVariables["color-mainIcon"],
                  cursor: "pointer",
                }}
                onClick={onCloseList}
              />
            </Box>
            <Box marginTop={2}>
              <DepositListTable
                list={deposits}
                isLoading={isLoadingList}
                noDataMessage="ไม่มีข้อมูลการฝากวันนี้"
                onClickBill={(id: string) => onOpenCompleteBillModal(id)}
                onClickBillCancel={(id: string) => onOpenCompleteBillModal(id)}
                onClickCancel={(deposit: DepositModel) => {
                  onOpenCancelBillModal(deposit);
                }}
              />
            </Box>
          </>
        </Drawer>
        {/* /.Table List */}

        {/* Preview Complete Bill Modal */}
        <DepositBillModal
          isOpen={isCompleteBillModalOpen}
          isComplete={true}
          isCancel={Boolean(deposit?.cancelAt)}
          onPrint={() => {
            onPrintBillPdfHistory(
              depositRenew?.receiptCopy?.id || deposit?.receiptCopy?.id || "",
              true
            );
          }}
          onClose={onCloseCompleteBillModal}
        >
          <DepositBill
            deposit={deposit}
            depositRenew={depositRenew}
            customer={customer}
            isComplete={true}
            isBillRenew={isBillRenew}
            souvenirs={deposit?.souvenirs ?? []}
          />
        </DepositBillModal>
        {/* Preview Complete Bill Modal */}

        {/* Preview Complete Cancel Bill Modal */}
        <BillModal
          isOpen={isCompleteBillCancelModalOpen}
          title={"ยกเลิก ฝาก (สำเนา)"}
          isComplete={true}
          onPrint={() =>
            onPrintBillPdfHistory(deposit?.cancelReceiptCopy?.id || "", false)
          }
          onClose={onCloseCompleteCancelBillModal}
        >
          <DepositCancelBill
            isBillRenew={isBillRenew}
            deposit={deposit}
            depositRenew={depositRenew}
          />
        </BillModal>
        {/* Preview Complete Cancel Bill Modal */}

        {/* Error Modal */}
        <InformationModal
          isOpen={isFailModalOpen}
          title="ทำรายการไม่สำเร็จ"
          onClose={onCloseFailModal}
        >
          <>
            <Box textAlign={"center"}>
              <Typography variant="body2" fontWeight={"regular"}>
                {messageFailModal}
              </Typography>
            </Box>
            <Button
              variant="contained"
              fullWidth
              sx={{ marginTop: 5 }}
              onClick={onCloseFailModal}
            >
              ตกลง
            </Button>
          </>
        </InformationModal>
        {/* /.Error Modal */}

        {/* Cancel Bill Modal */}
        <CancelBillModal
          isOpen={isCancelBillModalOpen}
          isCheckPinCode={!deposit?.refInvoiceNoPrev}
          limit={submitCancelLimit}
          remaining={submitCancelRemaining}
          retryAfter={submitCancelAfter}
          onClose={onCloseCancelBillModal}
          onSubmit={onCancelDeposit}
        />
        {/* /.Cancel Bill Modal */}

        {/* Souvenir Selection Warning Modal */}
        <SouvenirSelectionWarningModal
          isOpen={isSouvenirSelectionWarningModalOpen}
          onClose={() => setIsSouvenirSelectionWarningModalOpen(false)}
          onBackToSouvenirs={() => {
            void onBackToSouvenirs("deposit-souvenir-information-header");
          }}
          onContinue={() => setIsBillModalOpen(true)}
        />
        {/* /.Souvenir Selection Warning Modal */}

        <LoadProgressing isOpen={isLoadProgressing || isCheckingSouvenirs} />

        <AlertSnackbar
          type="success"
          isOpen={isOpenSuccessSnackbar}
          description="สร้างรายการฝากสำเร็จ"
          onClose={() => setIsOpenSuccessSnackbar(false)}
        />
      </>
    </MainLayout>
  );
});

export default Deposit;
